/**
 * Browse library media a page at a time (GET content-library), for editors' palettes and pick dialogs: search and
 * type run on the server, `loadMore` asks for the next page. Every record seen is kept in `records`, so an editor can
 * show what was picked without loading the whole library.
 */
import { computed, onBeforeUnmount, type Ref, ref, shallowRef, watch } from 'vue';
import type { LibraryType, MediaAssetRecord } from '@codot/contracts';
import { libraryApi } from '../api/content.api';

/** Every media type a player can show (no audio, no campaigns or playlists). */
export const VISUAL_MEDIA: LibraryType[] = ['image', 'video', 'app', 'composition', 'pdf'];

export function useLibraryBrowser(opts: {
  q: Ref<string>;
  /** Which media types (at least one). */
  types: Ref<LibraryType[]>;
  /** Only ready media (the default), or every state. */
  readyOnly?: boolean;
  /** Fetch only while true (e.g. a dialog that's open); defaults to always. */
  enabled?: Ref<boolean>;
}) {
  const items = shallowRef<MediaAssetRecord[]>([]);
  const records = shallowRef(new Map<string, MediaAssetRecord>());
  const total = ref(0);
  const next = ref<number | null>(null);
  const loading = ref(false);
  const more = computed(() => next.value !== null);
  let generation = 0;
  let appending: Promise<void> | null = null;

  async function fetchPage(offset: number): Promise<void> {
    const gen = offset === 0 ? ++generation : generation;
    loading.value = true;
    try {
      const page = await libraryApi.page({
        q: opts.q.value.trim(),
        types: opts.types.value.join(','),
        ready: opts.readyOnly === false ? 'false' : 'true',
        offset,
      });
      if (gen !== generation) return;
      const byId = new Map(page.media.map((a) => [a.id, a] as const));
      records.value = new Map([...records.value, ...byId]);
      const rows = page.items.flatMap((r) => (r.kind === 'media' && byId.has(r.id) ? [byId.get(r.id)!] : []));
      items.value = offset ? [...items.value, ...rows.filter((a) => !items.value.some((x) => x.id === a.id))] : rows;
      total.value = page.total;
      next.value = page.nextOffset;
    } finally {
      if (gen === generation) loading.value = false;
    }
  }
  function loadMore(): Promise<void> {
    if (appending || next.value === null) return appending ?? Promise.resolve();
    appending = fetchPage(next.value).finally(() => (appending = null));
    return appending;
  }
  const reload = () => fetchPage(0).catch(() => undefined);

  let timer: ReturnType<typeof setTimeout> | undefined;
  const on = () => opts.enabled?.value ?? true;
  watch(opts.types, () => on() && void reload());
  watch(opts.q, () => {
    clearTimeout(timer);
    timer = setTimeout(() => on() && void reload(), 250);
  });
  if (opts.enabled) watch(opts.enabled, (v) => v && void reload(), { immediate: true });
  else void reload();
  onBeforeUnmount(() => clearTimeout(timer));

  return { items, records, total, more, loading, loadMore, reload };
}
